import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, MapPin, ChevronRight } from 'lucide-react';
import { Card } from './ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * MoreFromHost — upcoming activities by the same host, chronological.
 * No ranking, no follower system, no profiles. Quiet continuity layer.
 * Renders nothing when the host has no other upcoming public activities.
 */
export function MoreFromHost({ activityId, hostName }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/api/activities/${activityId}/more-from-host`,
          { params: { limit: 5 }, withCredentials: true },
        );
        if (!cancelled) setItems(res.data?.activities || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activityId]);

  if (loading || items.length === 0) return null;

  return (
    <Card
      className="p-4"
      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
      data-testid="more-from-host"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
          More from {hostName || 'this host'}
        </p>
      </div>
      <div className="space-y-2">
        {items.map(a => {
          const locStr = typeof a.location === 'string' ? a.location : (a.location?.name || '');
          const when = a.start_time
            ? new Date(a.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
            : '';
          return (
            <button
              key={a.activity_id}
              type="button"
              onClick={() => navigate(`/activities/${a.activity_id}`)}
              className="w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors hover:bg-[var(--fy-surface-muted)] active:scale-[0.99]"
              data-testid={`more-from-host-${a.activity_id}`}
            >
              <div
                className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0"
                style={{ backgroundColor: 'var(--fy-surface-muted)' }}
              >
                {a.image_url ? (
                  <img src={a.image_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, #E4EADD 0%, #F3ECE0 100%)' }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>
                  {a.title}
                </p>
                <div className="flex items-center gap-2 text-[11px] mt-0.5" style={{ color: 'var(--fy-text-secondary)' }}>
                  {when && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{when}</span>}
                  {locStr && (
                    <span className="flex items-center gap-0.5 truncate">
                      <MapPin className="w-3 h-3" />
                      {locStr.length > 22 ? locStr.slice(0, 22) + '…' : locStr}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
            </button>
          );
        })}
      </div>
    </Card>
  );
}
